import { useState } from 'react';

export type TrendChartPoint = {
  date: string;
  value: number;
};

type Props = {
  points: TrendChartPoint[];
  formatValue: (value: number) => string;
};

const WIDTH = 720;
const HEIGHT = 132;
const PAD_LEFT = 50;
const PAD_RIGHT = 10;
const PAD_TOP = 10;
const PAD_BOTTOM = 18;
const GRID_LINES = 3;

const PLOT_W = WIDTH - PAD_LEFT - PAD_RIGHT;
const PLOT_H = HEIGHT - PAD_TOP - PAD_BOTTOM;

/**
 * 纯 SVG 折线 + 面积图，不引第三方图表库。
 * 纵轴刻意不从 0 起：余额几百块时，从 0 起会把几块钱的波动压成一条直线。
 */
export default function BalanceTrendChart({ points, formatValue }: Props) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  if (points.length === 0) {
    return <div className="flex h-[132px] items-center justify-center text-xs text-light-muted">暂无数据</div>;
  }

  const values = points.map((point) => point.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const span = maxValue - minValue;
  const padding = span > 0 ? span * 0.18 : Math.max(1, Math.abs(maxValue) * 0.1 || 1);
  const yMin = minValue - padding;
  const yMax = maxValue + padding;

  const xAt = (index: number): number => {
    if (points.length === 1) return PAD_LEFT + PLOT_W / 2;
    return PAD_LEFT + (index / (points.length - 1)) * PLOT_W;
  };
  const yAt = (value: number): number => {
    const ratio = (value - yMin) / (yMax - yMin || 1);
    return PAD_TOP + (1 - ratio) * PLOT_H;
  };

  const linePath = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${xAt(index).toFixed(2)},${yAt(point.value).toFixed(2)}`)
    .join(' ');
  const areaPath = `${linePath} L${xAt(points.length - 1).toFixed(2)},${(PAD_TOP + PLOT_H).toFixed(2)} L${xAt(0).toFixed(2)},${(PAD_TOP + PLOT_H).toFixed(2)} Z`;

  const gridValues = Array.from({ length: GRID_LINES + 1 }, (_, index) => {
    return yMin + (index / GRID_LINES) * (yMax - yMin);
  });

  const labelStep = Math.max(1, Math.ceil(points.length / 7));
  const hovered = hoverIndex == null ? null : points[hoverIndex];

  const handleMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = (event.clientX - rect.left) / rect.width;
    const plotStart = PAD_LEFT / WIDTH;
    const plotEnd = (WIDTH - PAD_RIGHT) / WIDTH;
    const withinPlot = Math.min(1, Math.max(0, (ratio - plotStart) / (plotEnd - plotStart)));
    setHoverIndex(Math.round(withinPlot * (points.length - 1)));
  };

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="w-full"
      role="img"
      aria-label="余额趋势"
      onMouseMove={handleMove}
      onMouseLeave={() => setHoverIndex(null)}
    >
      <defs>
        <linearGradient id="balanceTrendFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2563eb" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#2563eb" stopOpacity="0.01" />
        </linearGradient>
      </defs>

      {gridValues.map((value) => {
        const y = yAt(value);
        return (
          <g key={`grid-${value}`}>
            <line
              x1={PAD_LEFT}
              y1={y}
              x2={WIDTH - PAD_RIGHT}
              y2={y}
              stroke="currentColor"
              strokeWidth={1}
              className="text-light-border"
              strokeDasharray="2 6"
            />
            <text
              x={PAD_LEFT - 8}
              y={y + 3.5}
              textAnchor="end"
              className="fill-light-muted text-[10px]"
            >
              {formatValue(value)}
            </text>
          </g>
        );
      })}

      <path d={areaPath} fill="url(#balanceTrendFill)" />
      <path d={linePath} fill="none" stroke="#2563eb" strokeWidth={1.5} strokeLinejoin="round" />

      {points.length === 1 && (
        <circle cx={xAt(0)} cy={yAt(points[0].value)} r={2.5} fill="#2563eb" />
      )}

      {points.map((point, index) => {
        if (index % labelStep !== 0 && index !== points.length - 1) return null;
        return (
          <text
            key={`label-${point.date}`}
            x={xAt(index)}
            y={HEIGHT - 4}
            textAnchor="middle"
            className="fill-light-muted text-[10px]"
          >
            {point.date.slice(5)}
          </text>
        );
      })}

      {hovered && hoverIndex != null && (
        <g>
          <line
            x1={xAt(hoverIndex)}
            y1={PAD_TOP}
            x2={xAt(hoverIndex)}
            y2={PAD_TOP + PLOT_H}
            stroke="#2563eb"
            strokeWidth={1}
            strokeOpacity="0.4"
          />
          <circle cx={xAt(hoverIndex)} cy={yAt(hovered.value)} r={2.5} fill="#2563eb" />
          <text
            x={WIDTH - PAD_RIGHT}
            y={PAD_TOP + 3}
            textAnchor="end"
            className="fill-light-text text-[10px] font-medium"
            style={{ paintOrder: 'stroke', stroke: 'white', strokeWidth: 3 }}
          >
            {`${hovered.date}  ${formatValue(hovered.value)}`}
          </text>
        </g>
      )}
    </svg>
  );
}
